/**
 * Round 90 — Getting to Know You card.
 *
 * Small, dismissible dashboard card that surfaces ONE Life Blueprint
 * question at a time. Warm partner language. Never blocks the
 * dashboard. Never re-appears once dismissed for 24h. Respects the
 * server-side 30-day change-check cooldown (server returns
 * `up_to_date: true` when the owner is fully caught up).
 */
import React, { useEffect, useState } from "react";
import api from "@/utils/api";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown, ChevronUp, Heart, ArrowRight, Check } from "lucide-react";

const DISMISS_KEY = "paygbot_life_blueprint_dismissed_until";
const MINIMIZE_KEY = "paygbot_life_blueprint_minimized";
const POSTPONE_HOURS = 24;

// Total number of Life Blueprint fields — mirrors backend FIELDS
// (services/life_blueprint.py). Keep in sync if backend adds fields.
const TOTAL_FIELDS = 11;

function _hoursFromNow(hours) {
  return Date.now() + hours * 60 * 60 * 1000;
}

function _isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Number(raw) > Date.now();
  } catch {
    return false;
  }
}

export default function GettingToKnowYouCard() {
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [question, setQuestion] = useState(null);      // {field, question, context} | null
  const [upToDate, setUpToDate] = useState(false);
  const [filledCount, setFilledCount] = useState(0);
  const [answer, setAnswer] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [error, setError] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [allAnswers, setAllAnswers] = useState(null);

  // ── Initial fetch ────────────────────────────────────────────
  useEffect(() => {
    if (_isDismissed()) return;
    try {
      const min = localStorage.getItem(MINIMIZE_KEY);
      if (min === "1") setMinimized(true);
    } catch (_e) {
      /* ignore storage errors */
    }
    _refresh();
  }, []);

  async function _refresh() {
    setError(null);
    try {
      const [nextRes, currentRes] = await Promise.all([
        api.get("/paygbot/life-blueprint/next-question"),
        api.get("/paygbot/life-blueprint"),
      ]);
      const next = nextRes.data || {};
      const current = currentRes.data || {};
      setQuestion(next.question || null);
      setUpToDate(!!next.up_to_date);
      setFilledCount(Array.isArray(current.filled_fields) ? current.filled_fields.length : 0);
      setAllAnswers(current);
      // Only show if there IS a question OR the user is
      // fully-completed (small "you're up to date" state).
      setVisible(Boolean(next.question) || Boolean(next.up_to_date));
    } catch (err) {
      // On API failure, silently hide — never break the dashboard.
      console.warn("[GettingToKnowYouCard] refresh failed", err);
      setError("Could not reach PayGBot right now.");
      setVisible(false);
    }
  }

  // ── Actions ─────────────────────────────────────────────────
  async function _handleSave() {
    if (!question || !answer.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (question.field === "__change_check__") {
        // "Has anything changed?" — the answer is free-text.
        // Simplest UX: assume the user typed the change, but we don't
        // know which field it targets. Fall back to storing it in
        // `notes` and marking change-check as done.
        await api.post("/paygbot/life-blueprint/change-check", {
          changed: true,
          updates: { notes: answer.trim() },
        });
      } else {
        await api.post("/paygbot/life-blueprint/answer", {
          field: question.field,
          value: answer.trim(),
        });
      }
      setAnswer("");
      setConfirmation(true);
      setTimeout(() => setConfirmation(false), 3500);
      await _refresh();
    } catch (err) {
      console.warn("[GettingToKnowYouCard] save failed", err);
      setError("I couldn't save that just now. Try again in a moment.");
    } finally {
      setSaving(false);
    }
  }

  function _handleAnswerLater() {
    try {
      localStorage.setItem(DISMISS_KEY, String(_hoursFromNow(POSTPONE_HOURS)));
    } catch (_e) { /* ignore */ }
    setVisible(false);
  }

  function _handleDismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, String(_hoursFromNow(POSTPONE_HOURS * 7))); // hide 1 week
    } catch (_e) { /* ignore */ }
    setVisible(false);
  }

  function _handleMinimize() {
    const next = !minimized;
    setMinimized(next);
    try {
      localStorage.setItem(MINIMIZE_KEY, next ? "1" : "0");
    } catch (_e) { /* ignore */ }
  }

  async function _handleChangeCheckNo() {
    // "No, nothing changed" — stamp the cooldown.
    setSaving(true);
    try {
      await api.post("/paygbot/life-blueprint/change-check", { changed: false });
      setConfirmation(true);
      setTimeout(() => setConfirmation(false), 3500);
      await _refresh();
    } catch {
      // silent — hide until next session
      setVisible(false);
    } finally {
      setSaving(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────
  if (!visible) return null;

  const percent = Math.round((filledCount / TOTAL_FIELDS) * 100);
  const isChangeCheck = question?.field === "__change_check__";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      className="mb-6 rounded-xl border border-white/10 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 backdrop-blur-sm"
      data-testid="getting-to-know-you-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center gap-2">
          <Heart size={18} className="text-emerald-400" weight="fill" />
          <h3 className="text-white text-sm font-medium">Getting to Know You</h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={_handleMinimize}
            className="text-white/40 hover:text-white/70 rounded p-1"
            aria-label={minimized ? "Expand" : "Minimize"}
            data-testid="gtky-minimize-btn"
          >
            {minimized ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
          <button
            type="button"
            onClick={_handleDismiss}
            className="text-white/40 hover:text-white/70 rounded p-1"
            aria-label="Dismiss"
            data-testid="gtky-dismiss-btn"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {!minimized && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 pb-4 overflow-hidden"
          >
            {/* Warm partner intro */}
            {!upToDate && (
              <p className="text-white/70 text-xs sm:text-sm mb-3 leading-snug">
                The more I understand the life you&apos;re building, the better I can support your business.
              </p>
            )}

            {/* Completed state */}
            {upToDate && !showEdit && (
              <div className="text-white/80 text-sm space-y-3" data-testid="gtky-uptodate">
                <p>Thank you — I have everything I need for now. I&apos;ll check in again in a few weeks.</p>
                <button
                  type="button"
                  onClick={() => setShowEdit(true)}
                  className="text-emerald-300 text-xs underline underline-offset-2"
                  data-testid="gtky-edit-btn"
                >
                  Edit my answers
                </button>
              </div>
            )}

            {/* Question state */}
            {question && !showEdit && (
              <div className="space-y-3" data-testid="gtky-question">
                <div>
                  <p className="text-white font-medium text-sm sm:text-base" data-testid="gtky-question-text">
                    {question.question}
                  </p>
                  {question.context && (
                    <p className="text-white/50 text-xs mt-1">{question.context}</p>
                  )}
                </div>

                {isChangeCheck ? (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={_handleChangeCheckNo}
                      disabled={saving}
                      className="px-3 py-1.5 text-xs rounded-lg bg-white/10 hover:bg-white/20 text-white disabled:opacity-50"
                      data-testid="gtky-nothing-changed-btn"
                    >
                      Nothing new — continue
                    </button>
                    <button
                      type="button"
                      onClick={() => setAnswer("")}
                      className="px-3 py-1.5 text-xs rounded-lg text-emerald-300 underline underline-offset-2"
                    >
                      Something changed — let me share
                    </button>
                  </div>
                ) : null}

                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Answer in your own words…"
                  rows={2}
                  className="w-full rounded-lg border border-white/10 bg-slate-900/60 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 resize-none"
                  data-testid="gtky-answer-input"
                  disabled={saving}
                />

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={_handleSave}
                    disabled={saving || !answer.trim()}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-medium disabled:opacity-50"
                    data-testid="gtky-save-btn"
                  >
                    {saving ? "Saving…" : "Save"}
                    {!saving && <ArrowRight size={12} />}
                  </button>
                  <button
                    type="button"
                    onClick={_handleAnswerLater}
                    disabled={saving}
                    className="px-3 py-1.5 rounded-lg text-white/60 hover:text-white/90 text-xs"
                    data-testid="gtky-answer-later-btn"
                  >
                    Answer later
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEdit(true)}
                    className="px-3 py-1.5 rounded-lg text-white/60 hover:text-white/90 text-xs"
                    data-testid="gtky-edit-btn"
                  >
                    Edit my answers
                  </button>
                </div>
              </div>
            )}

            {/* Edit-my-answers list */}
            {showEdit && allAnswers && (
              <EditAnswers
                answers={allAnswers}
                onClose={() => setShowEdit(false)}
                onSaved={_refresh}
              />
            )}

            {/* Confirmation */}
            {confirmation && (
              <div
                className="mt-3 flex items-start gap-2 text-emerald-300 text-xs"
                data-testid="gtky-confirmation"
              >
                <Check size={14} className="mt-[2px] shrink-0" />
                <p>
                  Thank you. I&apos;ll use this to make future recommendations fit you and your goals better.
                </p>
              </div>
            )}

            {/* Error */}
            {error && (
              <p className="mt-3 text-amber-300 text-xs" data-testid="gtky-error">
                {error}
              </p>
            )}

            {/* Progress */}
            <div className="mt-4 flex items-center gap-3">
              <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-emerald-400 transition-all duration-500"
                  style={{ width: `${Math.min(100, percent)}%` }}
                />
              </div>
              <span
                className="text-white/50 text-[11px] tabular-nums"
                data-testid="gtky-progress"
              >
                PayGBot understands you: {filledCount} of {TOTAL_FIELDS} answered
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}


// ── Edit-my-answers sub-view ──────────────────────────────────
const FIELD_LABELS = {
  why_started_business: "Why you started this business",
  life_goal: "The life you're building",
  biggest_current_challenge: "Biggest current challenge",
  what_owner_is_protecting: "What you're protecting",
  what_owner_never_wants_business_to_become:
    "What you never want this business to become",
  preferred_working_style: "Preferred working style",
  strengths: "Your strengths",
  interests: "Your interests",
  motivation: "What motivates you",
  success_definition: "How you define success",
  notes: "Notes",
};
const FIELD_ORDER = Object.keys(FIELD_LABELS);


function EditAnswers({ answers, onClose, onSaved }) {
  const [drafts, setDrafts] = useState(() =>
    FIELD_ORDER.reduce((acc, f) => {
      acc[f] = answers?.[f] || "";
      return acc;
    }, {})
  );
  const [savingField, setSavingField] = useState(null);
  const [savedField, setSavedField] = useState(null);

  async function _saveOne(field) {
    if (!drafts[field]?.trim()) return;
    setSavingField(field);
    try {
      await api.post("/paygbot/life-blueprint/answer", {
        field,
        value: drafts[field].trim(),
      });
      setSavedField(field);
      setTimeout(() => setSavedField(null), 2000);
      onSaved && onSaved();
    } catch (err) {
      console.warn("[EditAnswers] save failed", err);
    } finally {
      setSavingField(null);
    }
  }

  return (
    <div className="space-y-3" data-testid="gtky-edit-panel">
      <div className="flex items-center justify-between">
        <p className="text-white/70 text-xs">
          You can update anything below. I&apos;ll remember your latest answer.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="text-emerald-300 text-xs underline underline-offset-2"
          data-testid="gtky-close-edit-btn"
        >
          Close
        </button>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {FIELD_ORDER.map((f) => (
          <div key={f} className="rounded-lg bg-slate-900/40 border border-white/5 p-2">
            <div className="flex items-center justify-between mb-1">
              <label className="text-white/70 text-[11px]">{FIELD_LABELS[f]}</label>
              {savedField === f && (
                <span className="text-emerald-300 text-[10px]">Saved</span>
              )}
            </div>
            <textarea
              value={drafts[f]}
              onChange={(e) => setDrafts({ ...drafts, [f]: e.target.value })}
              rows={2}
              placeholder="—"
              className="w-full text-sm bg-transparent text-white placeholder-white/20 focus:outline-none resize-none"
              data-testid={`gtky-edit-${f}-input`}
            />
            {drafts[f] !== (answers?.[f] || "") && (
              <button
                type="button"
                onClick={() => _saveOne(f)}
                disabled={savingField === f}
                className="mt-1 px-2 py-0.5 text-[11px] rounded bg-emerald-500/80 hover:bg-emerald-400 text-white disabled:opacity-50"
                data-testid={`gtky-edit-${f}-save`}
              >
                {savingField === f ? "Saving…" : "Save"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
